import debounce from 'debounce';

// photo is of form https://images.unsplash.com/photo-xxxxxx?ixlib=rb-1.2.1
// if it's an unsplash photo. extract the `/photo-xxxxxx` portion
function filepathFromUrl(url: string) {
  try {
    const urlObj = new URL(url);
    return urlObj.pathname;
  } catch (e) {
    return undefined;
  }
}

const APP_ID = '7066';
const BASE_URL = 'https://views.unsplash.com/v?app_id=' + APP_ID;
const TRACK_BY_FILEPATH_URL_PREFIX = `${BASE_URL}&filepath=`;
const MAX_URL_LENGTH = 2083;
const MAX_CHUNK_LENGTH = MAX_URL_LENGTH - TRACK_BY_FILEPATH_URL_PREFIX.length;
const FILEPATH_SEPARATOR = ',';
const TRACKING_DEBOUNCE_MS = 1000;
const DEFAULT_INTERVAL_MS = 10 * 60 * 1000;

interface QueueEntry {
  filepath: string;
  intervalMs: number;
}

export function getTrackingUrl(filepaths: string[]) {
  return `${TRACK_BY_FILEPATH_URL_PREFIX}${filepaths
    .map((filepath) => encodeURIComponent(filepath))
    .join(FILEPATH_SEPARATOR)}`;
}

export function getTrackingUrls(
  filepaths: string[],
  maxChunkLength = MAX_CHUNK_LENGTH,
) {
  let currentChunk: string[] = [];
  const chunks: string[][] = [];

  let remainingLength = 0;

  filepaths.forEach((filepath) => {
    const encoded = encodeURIComponent(filepath);

    if (remainingLength < encoded.length + FILEPATH_SEPARATOR.length) {
      currentChunk = [];
      remainingLength = maxChunkLength;
      chunks.push(currentChunk);
    }
    currentChunk.push(filepath);
    remainingLength -= encoded.length + FILEPATH_SEPARATOR.length;
  });

  return chunks.map(getTrackingUrl);
}

// See Unsplash: Tracking and Attribution
// https://hello.atlassian.net/wiki/spaces/TRELLO/pages/563910515/Unsplash+Tracking+and+Attribution

export class Tracker {
  private trackingQueue: QueueEntry[] = [];
  private lastTrackedTimestamp: Map<string, number> = new Map();
  private sendQueuedDebounced: () => void;

  constructor() {
    this.sendQueuedDebounced = debounce(this.sendQueued, TRACKING_DEBOUNCE_MS);
  }

  private sendQueued() {
    const now = Date.now();

    const newFilepaths = this.trackingQueue
      .filter(({ filepath, intervalMs }) => {
        const lastTracked = this.lastTrackedTimestamp.get(filepath);
        return lastTracked === undefined || lastTracked + intervalMs < now;
      })
      .map(({ filepath }) => filepath);

    const uniqueNewFilepaths = Array.from(new Set(newFilepaths));

    getTrackingUrls(uniqueNewFilepaths).forEach((url) => fetch(url));

    newFilepaths.forEach((filepath) =>
      this.lastTrackedTimestamp.set(filepath, now),
    );

    this.trackingQueue = [];
  }

  trackOncePerInterval(
    urlOrUrls: string | string[],
    intervalMs: number = DEFAULT_INTERVAL_MS,
  ): void {
    if (!Array.isArray(urlOrUrls)) {
      return this.trackOncePerInterval([urlOrUrls], intervalMs);
    }

    const urls: string[] = urlOrUrls;

    const filepaths = urls
      .filter((url) => url.startsWith('https://images.unsplash.com/'))
      .map(filepathFromUrl)
      .filter((filepath): filepath is string => filepath !== undefined);

    if (filepaths.length > 0) {
      this.trackingQueue = [
        ...this.trackingQueue,
        ...filepaths.map((filepath) => ({ filepath, intervalMs })),
      ];
      this.sendQueuedDebounced();
    }
  }
}

export const UnsplashTracker = new Tracker();
